<template>
  <div class="layout_box">
    <Teleport to="body">
      <DynamicWatermark :content="['自然源数字', userStore.nickname + '' + currentTime]" />
    </Teleport>

    <div
      :class="classObj"
      class="app-wrapper"
      :style="{ '--current-color': theme }"
    >
      <navbar
        ref="navbarRef"
        @set-layout="setLayout"
      />
      <div class="centerMianBox">
        <side-bar
          v-if="!sidebar.hide"
          id="layout-sidebar"
          class="sidebar-container"
        />
        <div
          :class="{ hasTagsView: needTagsView, sidebarHide: sidebar.hide }"
          class="main-container"
        >
          <div :class="{ 'fixed-header': fixedHeader }">
            <tags-view v-if="needTagsView" />
          </div>
          <div
            ref="pageCenterHei"
            class="pageCneterBox"
            :style="{ '--app-main-height': hasVisibleTags ? 'calc(100vh - 120px)' : 'calc(100vh - 86px)' }"
          >
            <app-main />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from '@/store/modules/app';
import SideBar from './components/Sidebar/index.vue';
import { DEFAULT_PASSWORD_REMINDER_KEY_PREFIX, useUserStore } from '@/store/modules/user';
import { AppMain, Navbar, Settings, TagsView } from './components';
import { useSettingsStore } from '@/store/modules/settings';
import { useTagsViewStore } from '@/store/modules/tagsView';
import { useResizeObserver } from '@vueuse/core';
import DynamicWatermark from './components/DynamicWatermark.vue';
import { checkDefaultPasswordApi } from '@/api/brand/user';
import router from '@/router';
const appStore = useAppStore();
const pageCenterHei = ref(null);
const settingsStore = useSettingsStore();
const theme = computed(() => settingsStore.theme);
const sidebar = computed(() => useAppStore().sidebar);
const device = computed(() => useAppStore().device);
const currentTime = ref('');
const userStore = useUserStore(); // 你的动态文字
const { proxy } = getCurrentInstance() as ComponentInternalInstance;
const needTagsView = computed(() => settingsStore.tagsView);
const tagsViewStore = useTagsViewStore();
const hasVisibleTags = computed(() => needTagsView.value && tagsViewStore.visitedViews.some((tag) => tag.path !== '/index'));
const fixedHeader = computed(() => settingsStore.fixedHeader);
const classObj = computed(() => ({
  hideSidebar: !sidebar.value.opened,
  openSidebar: sidebar.value.opened,
  withoutAnimation: sidebar.value.withoutAnimation,
  mobile: device.value === 'mobile',
}));

const { width } = useWindowSize();
const WIDTH = 992; // refer to Bootstrap's responsive design

watchEffect(() => {
  appStore.toggleDevice(width.value - 1 < WIDTH ? 'mobile' : 'desktop');
});

const navbarRef = ref<InstanceType<typeof Navbar>>();
const settingRef = ref<InstanceType<typeof Settings>>();

/** 当前账号在本次登录会话内仅提醒一次默认密码风险。 */
const showDefaultPasswordWarning = async () => {
  if (!userStore.userId || !userStore.tenantId) return;

  const reminderKey = `${DEFAULT_PASSWORD_REMINDER_KEY_PREFIX}${userStore.userId}:${userStore.tenantId}`;
  if (sessionStorage.getItem(reminderKey)) return;

  try {
    const res = await checkDefaultPasswordApi();
    if (res.data !== true) return;
  } catch {
    // 默认密码检查失败时静默处理，不影响页面使用。
    return;
  }

  sessionStorage.setItem(reminderKey, '1');
  try {
    await ElMessageBox.confirm(
      proxy.$t('AccountCenter.defaultPasswordWarningMessage'),
      proxy.$t('AccountCenter.defaultPasswordWarningTitle'),
      {
        confirmButtonText: proxy.$t('AccountCenter.changePasswordNow'),
        cancelButtonText: proxy.$t('Shared.Close'),
        type: 'warning',
      },
    );
  } catch {
    return;
  }

  await nextTick();
  await router.push({ name: 'AccountCenterPassword' });
};

onMounted(() => {
  showDefaultPasswordWarning();
  formatTime();
  setInterval(() => {
    formatTime();
  }, 5000);
  nextTick(() => {
    navbarRef.value?.initTenantList();
    appStore.SetAppMainHei(pageCenterHei.value.offsetHeight);
  });
});
// 页签显隐也会改变内容高度，需同步使用该高度的业务表格。
useResizeObserver(pageCenterHei, () => {
  appStore.SetAppMainHei(pageCenterHei.value.offsetHeight);
});

const setLayout = () => {
  settingRef.value?.openSetting();
};
// 1. 定义一个响应式变量来存储格式化后的时间字符串

// 2. 定义一个函数，用于格式化 Date 对象为 "YYYY-MM-DD HH:mm:ss" 格式
const formatTime = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() 返回 0-11
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0'); // 新增：获取秒并补零
  // 返回数字格式，如：2023-10-27 15:30:45
  if (currentTime.value != `${year}年${month}月${day}日 ${hours}:${minutes}`) {
    currentTime.value = `${year}年${month}月${day}日 ${hours}:${minutes}`;
  }
};
</script>
<style scoped>
/* 1️⃣ 父容器：必须设置 relative，水印才能铺满它 */
.parent-box {
  position: relative;
  width: 100%;
  min-height: 500px; /* 父元素高度（你页面多高就多高） */
  border: 1px solid #eee;
}

/* 2️⃣ 内容层：在水印上方 */
.content {
  position: relative;
  z-index: 999999;
}

/* 3️⃣ 核心：水印div → 铺满整个父元素 + 平铺水印 */
.watermark {
  /* 🔥 关键：铺满父元素的终极写法 */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* 🔥 铺满结束 */

  z-index: 0;
  pointer-events: none; /* 不阻挡点击 */
  font-weight: 400; /* ✅ 你要的字重400 */
  font-size: 16px;
  color: #000;

  /* 🔥 水印重复铺满（纯CSS，火狐永不闪动） */
  background-image: repeating-linear-gradient(transparent 0px, transparent 120px, #fff 120px, #fff 121px);
  background-size: 180px 180px; /* 水印间距 */
  transform: rotate(-15deg);
}

/* 🔥 生成重复水印文字 */
.watermark::before {
  content: attr(data-text);
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  line-height: 180px;
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/mixin.scss';
@use '@/assets/styles/variables.module.scss' as *;
.centerMianBox {
  display: flex;
  width: 100vw;
  box-sizing: border-box;
  padding-right: 10px;
   padding-left: 10px;

  margin-top: 10px;
}

.pageCneterBox {
  overflow: hidden;
}

.layout_box {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: var(--el-bg-color-page) url('@/assets/images/bg.png') center / cover no-repeat;
  position: relative;
  font-family: var(--el-font-family);
}

.app-wrapper {
  @include mixin.clearfix;
  position: relative;
  height: 100%;
  width: 100%;
}

.fixed-header {
  position: fixed;
  top: 0;
  inset-inline-end: 0;
  z-index: 9;
  width: calc(100% - #{$base-sidebar-width});
  transition: width 0.28s;
  background: $fixed-header-bg;
}

.hideSidebar .fixed-header {
  width: calc(100% - 54px);
}

.sidebarHide .fixed-header {
  width: 100%;
}
</style>
